import React from "react";
import { useTheme } from "@/context/ThemeContext";

// PZeroLogo — the "Priority Zero" brand mark.
// Renders the actual uploaded SVG artifacts from /logos/.
//
// Files (served from /app/frontend/public/logos/):
//   /logos/left-light.svg     — mark + wordmark, horizontal, LIGHT strokes  (for dark surfaces)
//   /logos/left-dark.svg      — mark + wordmark, horizontal, DARK strokes   (for light surfaces)
//   /logos/center-light.svg   — mark + wordmark, stacked, LIGHT strokes
//   /logos/center-dark.svg    — mark + wordmark, stacked, DARK strokes
//
// Usage:
//   <PZeroLogo height={28} />                     // horizontal, auto theme-picked
//   <PZeroLogo variant="onDark"  height={28} />   // force light-fill  logo (for dark surface)
//   <PZeroLogo variant="onLight" height={36} />   // force dark-fill   logo (for light surface)
//   <PZeroLogo layout="center"   height={80} />   // stacked wordmark under mark
//   <PZeroLogo iconOnly height={22} />            // just the C+dot mark
//   <PZeroLogo alwaysOnDark height={28} />        // shortcut for surfaces that are ALWAYS dark
export default function PZeroLogo({
  height = 28,
  variant,            // "onLight" | "onDark" — force a variant (bypasses theme)
  layout = "left",    // "left" | "center"
  iconOnly = false,
  alwaysOnDark = false,
  className = "",
}) {
  const { theme } = useTheme();
  let onDarkSurface;
  if (variant === "onDark") onDarkSurface = true;
  else if (variant === "onLight") onDarkSurface = false;
  else onDarkSurface = alwaysOnDark || theme === "dark";

  const tone = onDarkSurface ? "light" : "dark"; // "light" = light-stroke svg for dark surfaces

  if (iconOnly) {
    // Just the mark (C + dot). Inlined to match the SVG files pixel-for-pixel.
    const cFill = onDarkSurface ? "#ECECEC" : "#000000";
    const dotFill = onDarkSurface ? "#FF5355" : "#CE0C0F";
    return (
      <span
        className={`inline-flex items-center ${className}`}
        aria-label="Priority Zero"
        data-testid="pzero-logo"
      >
        <svg
          height={height}
          width={height}
          viewBox="0 0 80 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M40 0C44.9764 0 49.7398 0.91069 54.1348 2.57129C51.002 5.03151 48.7553 8.56887 47.9512 12.625C45.4274 11.8932 42.7597 11.5 40 11.5C24.2599 11.5 11.5 24.2599 11.5 40C11.5 55.7401 24.2599 68.5 40 68.5C55.7401 68.5 68.5 55.7401 68.5 40C68.5 37.4751 68.17 35.0274 67.5537 32.6963C71.6381 31.9873 75.2219 29.8218 77.751 26.7529C79.2059 30.8992 80 35.3567 80 40C80 62.0914 62.0914 80 40 80C17.9086 80 0 62.0914 0 40C0 17.9086 17.9086 0 40 0Z"
            fill={cFill}
          />
          <path
            d="M74.6263 15.9495C74.6263 21.4723 70.1492 25.9495 64.6263 25.9495C59.1035 25.9495 54.6263 21.4723 54.6263 15.9495C54.6263 10.4266 59.1035 5.94946 64.6263 5.94946C70.1492 5.94946 74.6263 10.4266 74.6263 15.9495Z"
            fill={dotFill}
          />
        </svg>
      </span>
    );
  }

  const src = `/logos/${layout}-${tone}.svg`;
  return (
    <img
      src={src}
      alt="Priority Zero"
      data-testid="pzero-logo"
      style={{ height, width: "auto" }}
      className={`inline-block select-none pointer-events-none ${className}`}
      draggable={false}
    />
  );
}
